import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from '../../../redux';
import { startCreateUser, createUser, cancelCreateUser } from '../../../redux';
import { Ability, Button } from '../../../components';
import UserForm from '../UserForm/UserForm';
import { User } from '../../../models';

function UserCreate() {
    const { t }    = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { user, created } = useAppSelector((state: RootState) => state.users);
    const [botTokenModal, setBotTokenModal] = useState<string | null>(null);

    useEffect(() => {
        dispatch(startCreateUser({
            firstname: '',
            lastname: '',
            email: '',
            phone: '',
            role: User.ROLE_USER,
            work_rate: 100,
            account_type: User.ACCOUNT_TYPE_HUMAN,
            jwt_expires_in: '30d',
            notificationPreferences: {
                channels: ['email'],
            },
        }));
    }, [dispatch]);

    useEffect(() => {
        if (created && user?.creationToken) {
            setBotTokenModal(user.creationToken);
            return;
        }
        if (created && user?._id && !user?.creationToken)
            navigate(`/users/edit/${user._id}`);
    }, [created, user, navigate]);

    const submit = function (values: AnyObj) : void {
        dispatch(createUser(values));
    }

    const dismissBotTokenModal = () => {
        const id = user?._id;
        setBotTokenModal(null);
        dispatch(cancelCreateUser());
        if (id)
            navigate(`/users/edit/${id}`);
    };

    const copyBotToken = async () => {
        if (!botTokenModal)
            return;
        try {
            await navigator.clipboard.writeText(botTokenModal);
        } catch (_e) {
            /* ignore */
        }
    };

    return (
        <Ability can="user:create" redirect="/">
            <div className="UserCreate">
                <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-0 sm:px-6 lg:px-8">
                    <div className="my-2 mx-auto lg:flex lg:items-center lg:justify-between">
                        <div>
                            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                { t('users.add_a_user') }
                            </h2>
                        </div>
                    </div>
                </header>
                <main className="mx-6 my-6 shadow">
                    {user && (!user._id) && (
                        <UserForm isCreation={true} initialValues={user} onSubmit={submit} />
                    )}
                </main>
                {botTokenModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                        <div className="max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
                            <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">{t('users.bot_jwt_created_once')}</p>
                            <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">{t('users.bot_jwt_created_copy_now')}</p>
                            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                                <code className="block max-h-32 flex-1 overflow-auto rounded bg-gray-100 dark:bg-gray-700 p-2 text-xs text-gray-900 dark:bg-gray-900 dark:text-gray-100">{botTokenModal}</code>
                                <Button title={t('users.bot_jwt_copy')} color="secondary" icon="copy" onClick={copyBotToken} />
                            </div>
                            <div className="mt-4 flex justify-end">
                                <Button title={t('users.bot_jwt_continue')} color="primary" icon="check" onClick={dismissBotTokenModal} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Ability>
    );
}

export default UserCreate;
